import { NavLink, Outlet, useMatch } from "react-router";
import {
  FileText,
  LayoutGrid,
  Users,
  UserSquare,
  Shield,
  Clock,
  Bell,
  Phone,
  Code2,
  MapPin,
  Share2,
} from "lucide-react";
import type { ComponentType } from "react";

interface SubNavItem {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number; strokeWidth?: number }>;
  count?: number | string;
  enabled?: boolean;
}

interface SubNavSection {
  heading: string;
  items: SubNavItem[];
}

const SECTIONS: SubNavSection[] = [
  {
    heading: "Service offering",
    items: [
      { to: "/settings/catalog", label: "Catalog", icon: LayoutGrid, enabled: true },
      { to: "/settings/agreements", label: "Agreements", icon: FileText, enabled: true },
    ],
  },
  {
    heading: "Session customization",
    items: [
      { to: "/settings/phone-bridge", label: "Phone bridge", icon: Phone, enabled: true },
    ],
  },
  {
    heading: "Website",
    items: [
      { to: "/settings/website/custom-trackers", label: "Custom trackers", icon: Code2, enabled: true },
    ],
  },
  {
    heading: "Team",
    items: [
      { to: "/settings/providers", label: "Providers", icon: Users, enabled: false },
      { to: "/settings/portal-users", label: "Portal users", icon: UserSquare, enabled: false },
      { to: "/settings/roles", label: "Roles & permissions", icon: Shield, enabled: false },
    ],
  },
  {
    heading: "Practice",
    items: [
      { to: "/settings/contact", label: "Contact info", icon: MapPin, enabled: true },
      { to: "/settings/social", label: "Social links", icon: Share2, enabled: true },
      { to: "/settings/hours", label: "Hours & availability", icon: Clock, enabled: false },
      { to: "/settings/notifications", label: "Notifications", icon: Bell, enabled: false },
    ],
  },
];

function FullItem({ item }: { item: SubNavItem }) {
  const Icon = item.icon;
  if (!item.enabled) {
    return (
      <div
        aria-disabled="true"
        className="flex items-center justify-between px-3 py-2 rounded-md text-ink-5 cursor-not-allowed"
      >
        <span className="flex items-center gap-2.5">
          <Icon size={14} strokeWidth={1.8} />
          <span className="text-[13px]">{item.label}</span>
        </span>
        {item.count !== undefined && (
          <span className="text-[11px] text-ink-5 num">{item.count}</span>
        )}
      </div>
    );
  }
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        `flex items-center justify-between px-3 py-2 rounded-md transition-colors ${
          isActive
            ? "bg-paper-hi shadow-card text-ink"
            : "text-ink-3 hover:bg-paper-2"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span className="flex items-center gap-2.5">
            <Icon size={14} strokeWidth={1.8} />
            <span className={`text-[13px] ${isActive ? "font-medium" : ""}`}>
              {item.label}
            </span>
          </span>
          {item.count !== undefined && (
            <span className="text-[11px] text-ink-4 num">{item.count}</span>
          )}
        </>
      )}
    </NavLink>
  );
}

function CompactItem({ item }: { item: SubNavItem }) {
  const Icon = item.icon;
  if (!item.enabled) {
    return (
      <div
        aria-disabled="true"
        title={item.label}
        className="flex items-center justify-center w-10 h-10 rounded-md text-ink-5 cursor-not-allowed"
      >
        <Icon size={16} strokeWidth={1.8} />
      </div>
    );
  }
  return (
    <NavLink
      to={item.to}
      title={item.label}
      aria-label={item.label}
      className={({ isActive }) =>
        `flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
          isActive
            ? "bg-paper-hi shadow-card text-ink"
            : "text-ink-3 hover:bg-paper-2"
        }`
      }
    >
      <Icon size={16} strokeWidth={1.8} />
    </NavLink>
  );
}

export function SettingsShell() {
  // Compact mode for the deep editor screens (defaults editor, agreement
  // edit, catalog editors). The catalog rail collapses to icons and the
  // page header / breadcrumb hides since the editor screens render their own.
  const editingDefaults = !!useMatch("/settings/agreements/defaults");
  const editingAgreement = !!useMatch("/settings/agreements/:id");
  // Compact mode for the deep agreement editors. Catalog has its own
  // master-detail layout but keeps the full settings sub-nav visible.
  const compact = editingDefaults || editingAgreement;

  if (compact) {
    return (
      <div className="min-h-full flex">
        <aside className="hair-r border-r bg-paper flex-shrink-0">
          <nav className="px-2 py-3 sticky top-[var(--banner-h)]">
            {SECTIONS.map((section, i) => (
              <div
                key={section.heading}
                className={`flex flex-col items-center gap-1 ${
                  i === 0 ? "" : "pt-3 mt-3 border-t border-ink/10"
                }`}
              >
                {section.items.map((item) => (
                  <CompactItem key={item.label} item={item} />
                ))}
              </div>
            ))}
          </nav>
        </aside>
        <section className="flex-1 min-w-0">
          <Outlet />
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      {/* Settings has 14 items in 5 groups — too many for a tab strip, so the
          rail stays. The breadcrumb and title come from the TopBar like every
          other page; this section no longer renders a second bar of its own. */}
      <div className="px-4 md:px-7 pt-6 pb-20 grid grid-cols-12 gap-6">
        <aside className="col-span-12 lg:col-span-3">
          <nav aria-label="Settings" className="space-y-0.5 lg:sticky lg:top-6">
            {SECTIONS.map((section, i) => (
              <div key={section.heading} className={i === 0 ? "" : "pt-5"}>
                <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-semibold pb-2 px-3">
                  {section.heading}
                </div>
                {section.items.map((item) => (
                  <FullItem key={item.label} item={item} />
                ))}
              </div>
            ))}
          </nav>
        </aside>

        <section className="col-span-12 lg:col-span-9">
          <Outlet />
        </section>
      </div>
    </div>
  );
}
